import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { agencyService } from '../../services/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Plus, Building2, Edit, Power, BarChart3, Users, Car } from 'lucide-react';
import { toast } from 'sonner';

const statusLabels = {
  active: { label: 'Activa', variant: 'default', color: 'bg-green-100 text-green-800' },
  suspended: { label: 'Suspendida', variant: 'destructive', color: 'bg-yellow-100 text-yellow-800' },
  deleted: { label: 'Eliminada', variant: 'secondary', color: 'bg-red-100 text-red-800' }
};

const planLabels = {
  free: { label: 'Free', color: 'bg-gray-100 text-gray-800' },
  pro: { label: 'Pro', color: 'bg-blue-100 text-blue-800' },
  enterprise: { label: 'Enterprise', color: 'bg-purple-100 text-purple-800' }
};

const AdminAgenciesPage = () => {
  const [agencies, setAgencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});

  useEffect(() => {
    loadAgencies();
  }, []);

  const loadAgencies = async () => {
    try {
      const data = await agencyService.getAll();
      setAgencies(data);
      
      // Load stats for each agency
      const statsPromises = data.map(async (agency) => {
        try {
          const agencyStats = await agencyService.getStats(agency.id);
          return { id: agency.id, stats: agencyStats };
        } catch (error) {
          return { id: agency.id, stats: null };
        }
      });
      
      const allStats = await Promise.all(statsPromises);
      const statsMap = {};
      allStats.forEach(({ id, stats }) => {
        statsMap[id] = stats;
      });
      setStats(statsMap);
    } catch (error) {
      console.error('Error loading agencies:', error);
      toast.error('Error al cargar agencias');
    } finally {
      setLoading(false);
    }
  };

  const toggleStatus = async (agency) => {
    try {
      const newStatus = agency.status === 'active' ? 'suspended' : 'active';
      await agencyService.update(agency.id, { status: newStatus });
      toast.success('Estado actualizado');
      loadAgencies();
    } catch (error) {
      toast.error('Error al cambiar estado');
    }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-8">
        <div>
          <h1 className="text-2xl sm:text-4xl font-bold text-slate-900">Agencias</h1>
          <p className="text-slate-600 mt-2">Gestiona todas las agencias del sistema</p>
        </div>
        <Link to="/app/admin/agencies/new">
          <Button data-testid="new-agency-button">
            <Plus className="w-4 h-4 mr-2" />
            Nueva Agencia
          </Button>
        </Link>
      </div>

      {loading ? (
        <div className="text-center py-12">Cargando...</div>
      ) : agencies.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Building2 className="w-16 h-16 mx-auto text-slate-300 mb-4" />
            <p className="text-slate-600 mb-4">No hay agencias registradas</p>
            <Link to="/app/admin/agencies/new">
              <Button>Crear primera agencia</Button>
            </Link>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-6">
          {agencies.map((agency) => {
            const statusInfo = statusLabels[agency.status] || statusLabels.active;
            const planInfo = planLabels[agency.plan] || planLabels.free;
            const agencyStats = stats[agency.id];
            const publicProfile = agency.publicProfile || {};
            
            return (
              <Card key={agency.id} data-testid={`agency-card-${agency.id}`}>
                <CardHeader>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-2">
                        <CardTitle className="text-xl">{agency.name}</CardTitle>
                        <Badge className={statusInfo.color}>
                          {statusInfo.label}
                        </Badge>
                        <Badge className={planInfo.color}>
                          {planInfo.label}
                        </Badge>
                      </div>
                      <p className="text-sm text-slate-500">
                        Slug: <span className="font-mono">{agency.slug}</span>
                      </p>
                      {publicProfile.description && (
                        <p className="text-slate-600 mt-2">{publicProfile.description}</p>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Link to={`/app/admin/agencies/${agency.id}/edit`}>
                        <Button variant="outline" size="sm">
                          <Edit className="w-4 h-4 mr-1" />
                          Editar
                        </Button>
                      </Link>
                      <Button
                        variant={agency.status === 'active' ? 'destructive' : 'default'}
                        size="sm"
                        onClick={() => toggleStatus(agency)}
                      >
                        <Power className="w-4 h-4 mr-1" />
                        {agency.status === 'active' ? 'Suspender' : 'Activar'}
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                    <div>
                      <p className="text-slate-500 mb-1">Contacto</p>
                      <p className="font-medium">{publicProfile.emailPublic || 'No configurado'}</p>
                      <p className="text-slate-600">{publicProfile.phonePublic || '-'}</p>
                    </div>
                    <div>
                      <p className="text-slate-500 mb-1">Ubicación</p>
                      <p className="font-medium">{publicProfile.addressText || 'No configurado'}</p>
                    </div>
                    {agencyStats && (
                      <>
                        <div className="flex items-center gap-2">
                          <Users className="w-5 h-5 text-slate-400" />
                          <div>
                            <p className="text-slate-500 text-xs">Usuarios</p>
                            <p className="font-bold text-lg">{agencyStats.users || 0}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Car className="w-5 h-5 text-slate-400" />
                          <div>
                            <p className="text-slate-500 text-xs">Vehículos</p>
                            <p className="font-bold text-lg">
                              {agencyStats.vehicles?.published || 0} / {agencyStats.vehicles?.total || 0}
                            </p>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                  <div className="mt-4 flex gap-2">
                    <Link to={`/a/${agency.slug}`} target="_blank">
                      <Button variant="ghost" size="sm">Ver sitio público</Button>
                    </Link>
                    <Link to={`/app/admin/agencies/${agency.id}/stats`}>
                      <Button variant="ghost" size="sm">
                        <BarChart3 className="w-4 h-4 mr-1" />
                        Estadísticas
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminAgenciesPage;
